import type { ProjectConfig } from '/#/config'
import type { BeforeMiniState } from '/#/store'

import { defineStore } from 'pinia'
import { store } from '/@/store'
import { ref, computed } from 'vue'

import { ThemeEnum } from '/@/enums/appEnum'
import { APP_DARK_MODE_KEY_, PROJ_CFG_KEY } from '/@/enums/cacheEnum'
import { Persistent } from '/@/utils/cache/persistent'
import { darkMode } from '/@/settings/designSetting'
import { resetRouter } from '/@/router'
import { merge as deepMerge } from 'lodash-es'

let timeId: TimeoutHandle

export const useAppStore = defineStore('app', () => {
  // State
  const darkModeRef = ref<ThemeEnum | undefined>(ThemeEnum.DARK)
  const pageLoading = ref(false)
  const projectConfig = ref<ProjectConfig | null>(Persistent.getLocal(PROJ_CFG_KEY))
  const beforeMiniInfo = ref<BeforeMiniState>({})

  // Getters
  const getPageLoading = computed(() => pageLoading.value)
  const getDarkMode = computed<'light' | 'dark' | string>(
    () => darkModeRef.value || localStorage.getItem(APP_DARK_MODE_KEY_) || darkMode,
  )
  const getBeforeMiniInfo = computed(() => beforeMiniInfo.value)
  const getProjectConfig = computed(() => projectConfig.value || ({} as ProjectConfig))
  const getHeaderSetting = computed(() => getProjectConfig.value.headerSetting)
  const getMenuSetting = computed(() => getProjectConfig.value.menuSetting)
  const getTransitionSetting = computed(() => getProjectConfig.value.transitionSetting)
  const getMultiTabsSetting = computed(() => getProjectConfig.value.multiTabsSetting)

  // Actions
  function setPageLoading(loading: boolean): void {
    pageLoading.value = loading
  }

  function setDarkMode(mode: ThemeEnum): void {
    darkModeRef.value = mode
    localStorage.setItem(APP_DARK_MODE_KEY_, mode)
    document.documentElement.setAttribute('data-theme', mode || 'light')
  }

  function setBeforeMiniInfo(state: BeforeMiniState): void {
    beforeMiniInfo.value = state
  }

  function setProjectConfig(config: DeepPartial<ProjectConfig>): void {
    projectConfig.value = deepMerge(projectConfig.value || {}, config)
    Persistent.setLocal(PROJ_CFG_KEY, projectConfig.value)
  }

  async function resetAllState() {
    resetRouter()
    Persistent.clearAll()
  }

  async function setPageLoadingAction(loading: boolean): Promise<void> {
    if (loading) {
      clearTimeout(timeId)
      // Prevent flicker
      timeId = setTimeout(() => {
        setPageLoading(loading)
      }, 50)
    } else {
      setPageLoading(loading)
      clearTimeout(timeId)
    }
  }

  return {
    darkMode: darkModeRef,
    pageLoading,
    projectConfig,
    beforeMiniInfo,
    getPageLoading,
    getDarkMode,
    getBeforeMiniInfo,
    getProjectConfig,
    getHeaderSetting,
    getMenuSetting,
    getTransitionSetting,
    getMultiTabsSetting,
    setPageLoading,
    setDarkMode,
    setBeforeMiniInfo,
    setProjectConfig,
    resetAllState,
    setPageLoadingAction,
  }
})

// Need to be used outside the setup
export function useAppStoreWithOut() {
  return useAppStore(store)
}
